import React, { useRef, useState, useCallback } from 'react';
import { useAuth } from '../../helpers/AuthContext';

function uploadFileToStorage(uploadUrl, file, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
                onProgress((event.loaded / event.total) * 100);
            }
        });
        xhr.onload = () => {
            if (xhr.status === 200) {
                resolve();
            } else {
                let msg = 'Failed to upload file';
                try {
                    const resp = JSON.parse(xhr.responseText);
                    if (resp && resp.message) msg = resp.message;
                } catch {}
                reject(new Error(msg));
            }
        };
        xhr.onerror = () => reject(new Error('Upload failed. Please try again.'));
        xhr.onabort = () => reject(new Error('Upload aborted.'));
        xhr.ontimeout = () => reject(new Error('Upload timed out.'));
        xhr.send(file);
    });
}

export default function MultiUploader({
    accept = '',
    maxFiles = 10,
    onUploadSuccess,
    onChange,
    name,
    value,
    initialValue,
}) {
    const { fetch, API_URL } = useAuth();
    const fileInputRef = useRef();
    const [fileEntries, setFileEntries] = useState([]);
    const completedResultsRef = useRef([]);

    const anyUploading = fileEntries.some((e) => e.status === 'uploading');

    const updateEntry = useCallback((localId, patch) => {
        setFileEntries((prev) =>
            prev.map((e) => (e.localId === localId ? { ...e, ...patch } : e))
        );
    }, []);

    const uploadOne = useCallback(async (localId, file) => {
        updateEntry(localId, { status: 'uploading', progress: 0, error: '' });

        try {
            // Step 1: Get presigned upload URL
            const urlRes = await fetch(`${API_URL}/v1/seller/media/upload-get-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ filename: file.name, size: file.size, mimetype: file.type }),
            });
            if (!urlRes.ok) {
                const data = await urlRes.json().catch(() => ({}));
                throw new Error(data.message || 'Failed to get upload URL');
            }
            const uploadUrlData = await urlRes.json();

            // Step 2: Upload directly to storage
            await uploadFileToStorage(uploadUrlData.uploadUrl, file, (progress) => {
                updateEntry(localId, { progress });
            });

            // Step 3: Finalize — create DB record
            const finalizeRes = await fetch(`${API_URL}/v1/seller/media/upload-finalize`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(uploadUrlData),
            });
            if (!finalizeRes.ok) {
                const data = await finalizeRes.json().catch(() => ({}));
                throw new Error(data.message || 'Failed to finalize upload');
            }
            const { file: resultFile } = await finalizeRes.json();

            updateEntry(localId, { status: 'done', progress: 100, result: resultFile });

            completedResultsRef.current = [...completedResultsRef.current, resultFile];
            if (onChange) {
                onChange(completedResultsRef.current.map((f) => f.id));
            }
        } catch (err) {
            updateEntry(localId, { status: 'error', progress: 0, error: err.message || 'Upload failed.' });
        }
    }, [fetch, API_URL, onChange, updateEntry]);

    const handleFileChange = useCallback((e) => {
        const selected = Array.from(e.target.files);
        if (!selected.length) return;

        const limited = maxFiles ? selected.slice(0, maxFiles) : selected;

        const newEntries = limited.map((file) => ({
            localId: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
            file,
            status: 'pending',
            progress: 0,
            error: '',
            result: null,
        }));

        setFileEntries((prev) => {
            const next = [...prev, ...newEntries];
            return next;
        });

        // Reset input so the same files can be re-selected if needed
        if (fileInputRef.current) fileInputRef.current.value = '';

        // Start all uploads concurrently
        newEntries.forEach(({ localId, file }) => uploadOne(localId, file));
    }, [maxFiles, uploadOne]);

    // Fire onUploadSuccess when all entries are settled and at least one succeeded
    const prevFileEntriesRef = useRef([]);
    React.useEffect(() => {
        if (!fileEntries.length) return;
        const allSettled = fileEntries.every((e) => e.status === 'done' || e.status === 'error');
        const prevAllSettled = prevFileEntriesRef.current.every(
            (e) => e.status === 'done' || e.status === 'error'
        );
        if (allSettled && !prevAllSettled && onUploadSuccess) {
            const results = fileEntries.filter((e) => e.status === 'done').map((e) => e.result);
            if (results.length) onUploadSuccess(results);
        }
        prevFileEntriesRef.current = fileEntries;
    }, [fileEntries, onUploadSuccess]);

    const doneCount = fileEntries.filter((e) => e.status === 'done').length;
    const totalCount = fileEntries.length;

    return (
        <div className="multi-uploader">
            <div className="group">
                <input
                    ref={fileInputRef}
                    type="file"
                    name={name}
                    accept={accept}
                    multiple
                    disabled={anyUploading}
                    onChange={handleFileChange}
                    className="multi-uploader-input"
                />
                {maxFiles && (
                    <p className="help">You may select up to {maxFiles} files at a time.</p>
                )}
            </div>

            {fileEntries.length > 0 && (
                <div className="multi-uploader-list" style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {totalCount > 1 && (
                        <p className="help" style={{ margin: '0 0 0.25rem' }}>{doneCount} of {totalCount} uploaded</p>
                    )}
                    {fileEntries.map((entry) => (
                        <div
                            key={entry.localId}
                            className={`multi-uploader-item status-${entry.status}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                padding: '0.4rem 0.65rem',
                                borderRadius: '5px',
                                fontSize: '0.875rem',
                                background: entry.status === 'done' ? '#eaf7ee'
                                    : entry.status === 'error' ? '#fdecea'
                                    : entry.status === 'uploading' ? '#eef4fd'
                                    : '#f4f4f4',
                            }}
                        >
                            <span className="multi-uploader-filename" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {entry.file.name}
                            </span>
                            {entry.status === 'uploading' && (
                                <progress value={entry.progress} max={100} className="multi-uploader-progress" style={{ width: '120px', flexShrink: 0 }} />
                            )}
                            {entry.status === 'done' && (
                                <span className="success" style={{ fontWeight: 500, flexShrink: 0 }}>&#10003; Uploaded</span>
                            )}
                            {entry.status === 'error' && (
                                <span className="error" style={{ flexShrink: 0 }}>{entry.error}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
